import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import { slugDeServico } from '../../../shared/utils/slug.util';
import {
  CategoriaComServicosPublicaResponse,
  CategoriaPublicaResponse,
  ServicoPublicoResponse,
  TodosServicosCategoria,
  TodosServicosItem,
} from '../models/todos-servicos.model';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico`;

/** Ícone de menu para categoria que o adm cadastrou sem chave, ou com uma fora do catálogo. */
const ICONE_PADRAO = 'layout-grid';

/**
 * Chave editorial do adm → nome do ícone no Lucide.
 *
 * ⚠️ **O que a API manda NÃO é nome de ícone Lucide.** `des_icone_ctg` guarda a chave do
 * catálogo do portal administrativo (`ICONES_CATEGORIA`, em `servicos.model.ts` do
 * sai-adm-web): 'saude', 'educacao', 'social'… O Lucide não conhece nenhuma delas e
 * **lança** `Unable to resolve icon` quando recebe um nome que não existe. A exceção
 * aborta a renderização do template no meio — derrubando o resto do menu e o painel de
 * serviços junto —, e por isso a tradução é obrigatória, não um detalhe cosmético.
 *
 * Este mapa espelha, chave por chave, o catálogo do adm, inclusive onde o par não é
 * óbvio ('educacao' é `graduation-cap`, não `book-open`; 'social' é `hand-heart`). Ao
 * acrescentar uma opção lá, acrescente aqui: chave ausente cai no ícone padrão, o que
 * degrada bem, mas mostra o ícone errado.
 */
const ICONES_POR_CHAVE: Readonly<Record<string, string>> = {
  saude: 'heart',
  educacao: 'graduation-cap',
  social: 'hand-heart',
  obras: 'hard-hat',
  financas: 'landmark',
  transporte: 'bus',
  ambiente: 'leaf',
  cultura: 'palette',
  seguranca: 'shield',
};

/**
 * O rótulo do botão que abre o serviço.
 *
 * É único, e é o da tela de referência: a ação é sempre a mesma — ir para a página do
 * serviço, onde o cidadão lê a carta e decide se solicita. Quem distingue solicitação,
 * agendamento e atendimento presencial é aquela página, a partir dos tipos de
 * atendimento, e não este botão.
 */
const ROTULO_ACAO = 'Acesse aqui';

/** O erro fica local: a tela é pública e trata a falha ela mesma, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/**
 * O catálogo público da carta de serviços — o que alimenta a tela "Todos os Serviços".
 *
 * ⚠️ **O portal não sabe nem envia o órgão.** O back-end o resolve pelo host de quem
 * chamou (`Origin`, e `Host` na ausência dele) contra um mapa do `appsettings`; por isso
 * nenhuma das duas rotas tem parâmetro. Host não mapeado responde 200 com lista vazia,
 * igual a órgão sem catálogo — a tela não distingue os dois, nem deve.
 *
 * As duas chamadas são complementares e ambas são feitas:
 *
 * - `GET /categoria` monta o **menu**, e é o único que traz a categoria **sem serviço**
 *   (com `numTotalServicos = 0`) e a ordem editorial completa;
 * - `GET /servico` traz os **grupos** com os serviços de cada categoria.
 *
 * Nem busca nem paginação: as duas listas vêm inteiras e a tela filtra no cliente, que é
 * o que o contrato do back-end assume.
 */
@Service()
export class CatalogoPublicoService {
  private readonly categoriasApi = httpResource<ResponseApi<CategoriaPublicaResponse[]>>(() => ({
    url: `${RECURSO}/categoria`,
    context: ERRO_LOCAL(),
  }));

  private readonly gruposApi = httpResource<
    ResponseApi<CategoriaComServicosPublicaResponse[]>
  >(() => ({
    url: `${RECURSO}/servico`,
    context: ERRO_LOCAL(),
  }));

  /** Carregando enquanto qualquer uma das duas chamadas estiver no ar. */
  readonly carregando = computed(
    () => this.categoriasApi.isLoading() || this.gruposApi.isLoading()
  );

  /**
   * Falha só quando o **menu** não veio: sem ele não há tela. Se apenas os grupos
   * falharem, o menu aparece e cada categoria mostra "Nenhum serviço cadastrado".
   */
  readonly falhou = computed(() => this.categoriasApi.error() !== undefined);

  /**
   * O menu e os serviços já cruzados, na ordem editorial do adm.
   *
   * O cruzamento é pelo `codCategoriaServicoCtg`: categoria que o menu traz e os grupos
   * não fica com a lista vazia — é o caso normal de `numTotalServicos = 0`.
   */
  readonly categorias = computed<readonly TodosServicosCategoria[]>(() => {
    const menu = dados(this.categoriasApi);
    const grupos = dados(this.gruposApi);

    const servicosPorCategoria = new Map(
      grupos.map((grupo) => [grupo.codCategoriaServicoCtg, grupo.servicos] as const)
    );

    return [...menu]
      .sort((a, b) => a.numOrdemCtg - b.numOrdemCtg)
      .map((categoria) => ({
        id: String(categoria.codCategoriaServicoCtg),
        titulo: categoria.desNomeCtg,
        icone: normalizarIcone(categoria.desIconeCtg),
        cor: categoria.desCorTemaCtg,
        ordem: categoria.numOrdemCtg,
        totalServicos: categoria.numTotalServicos,
        servicos: (servicosPorCategoria.get(categoria.codCategoriaServicoCtg) ?? []).map(
          paraItem
        ),
      }));
  });

  /**
   * O menu como veio da API, na ordem da resposta — par de `grupos`, para os serviços do
   * domínio que traduzem o catálogo para a própria tela (ex.: Categorias de Serviços), e não
   * para componentes.
   *
   * Mantém a chave editorial do ícone, que `categorias` já traduziu para o Lucide, e os
   * campos que o menu de "Todos os Serviços" não usa (descrição, tipos).
   */
  readonly menu = computed<readonly CategoriaPublicaResponse[]>(() => dados(this.categoriasApi));

  /**
   * Carregando enquanto o menu estiver no ar — para a tela que depende só dele e não deve
   * esperar os grupos, que são a chamada mais pesada.
   */
  readonly carregandoMenu = computed(() => this.categoriasApi.isLoading());

  /** Carregando enquanto os grupos de serviços estiverem no ar. */
  readonly carregandoServicos = computed(() => this.gruposApi.isLoading());

  /** Falha dos grupos — a única chamada de que a lista de nomes depende. */
  readonly falhouServicos = computed(() => this.gruposApi.error() !== undefined);

  /**
   * Os grupos como vieram da API, na ordem editorial — para os serviços do domínio que
   * traduzem o catálogo para a própria tela (ex.: Serviços Digitais), e não para componentes.
   *
   * Vem só dos grupos, sem cruzar com o menu, pelo mesmo motivo de `nomesServicos`: a falha
   * do menu não deve esvaziar uma tela que não depende dele. E mantém a chave editorial do
   * ícone, que `categorias` já traduziu para o Lucide.
   */
  readonly grupos = computed<readonly CategoriaComServicosPublicaResponse[]>(() =>
    dados(this.gruposApi)
  );

  /**
   * Os nomes dos serviços publicáveis, sem repetição e em ordem alfabética — é a lista do
   * filtro "Serviços vinculados" do acompanhamento.
   *
   * Vem só dos grupos, sem cruzar com o menu: para listar serviços o menu não é preciso, e a
   * falha dele não deve esvaziar o filtro. A deduplicação é pelo nome porque é o nome que a
   * consulta por protocolo devolve (`desNomeServicoSsv`) e, portanto, o que o filtro compara.
   */
  readonly nomesServicos = computed<readonly string[]>(() => {
    const nomes = dados(this.gruposApi)
      .flatMap((grupo) => grupo.servicos)
      .map((servico) => servico.desNomeSrv.trim())
      .filter((nome) => nome.length > 0);

    return [...new Set(nomes)].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  });

  /** Refaz as duas chamadas — é o "Tentar novamente" do aviso de falha. */
  recarregar(): void {
    this.categoriasApi.reload();
    this.gruposApi.reload();
  }

  /** Refaz só os grupos — o "Tentar novamente" de quem depende apenas dos serviços. */
  recarregarServicos(): void {
    this.gruposApi.reload();
  }
}

/**
 * O conteúdo do envelope, ou lista vazia.
 *
 * O `error()` é consultado ANTES do `value()` de propósito: em estado de erro o
 * `value()` **lança**, e a tela que mostra o aviso de falha é a mesma que lê esta lista.
 * Sem esta guarda, a falha de uma das duas chamadas derrubaria o `computed` inteiro.
 */
function dados<T>(recurso: {
  error: () => unknown;
  value: () => ResponseApi<T[]> | undefined;
}): readonly T[] {
  if (recurso.error() !== undefined) return [];

  return recurso.value()?.data ?? [];
}

/**
 * O recorte público do serviço vira item da lista.
 *
 * **Não há campo de link no contrato**: `tb_cse_servico` não tem coluna de link e o
 * wizard do adm não tem onde preenchê-la. O destino é a página do serviço, e a rota é
 * o **slug do nome** — decisão de apresentação, não dado da API (ver `slugDeServico`).
 *
 * Todo serviço publicável ganha o botão, sem depender dos tipos de atendimento: a
 * página de destino existe para qualquer um deles, inclusive o meramente informativo,
 * que é justamente onde o cidadão lê a carta do serviço.
 */
function paraItem(servico: ServicoPublicoResponse): TodosServicosItem {
  return {
    id: String(servico.codServicoSrv),
    titulo: servico.desNomeSrv,
    descricao: servico.desResumoSrv?.trim() || 'Descrição não informada.',
    tiposAtendimento: servico.tiposAtendimento,
    permiteAnonimo: servico.bitPermitirAnonimoSrv,
    possuiImagem: servico.bitPossuiImagemSrv === true,
    link: `/${slugDeServico(servico.desNomeSrv)}`,
    rotuloLink: ROTULO_ACAO,
  };
}

/**
 * A chave editorial do adm traduzida para o nome do ícone no Lucide.
 *
 * Chave fora do catálogo cai no ícone padrão — inclusive vazia, nula ou com um nome de
 * arquivo antigo. **Nunca devolve a chave crua**: um nome que o Lucide não conhece faz
 * o `<i-lucide>` lançar e leva a tela inteira junto.
 */
function normalizarIcone(chave: string | null | undefined): string {
  const limpa = (chave ?? '').trim().toLowerCase();

  return ICONES_POR_CHAVE[limpa] ?? ICONE_PADRAO;
}
